import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    setIsLangOpen(false);
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b-2 border-stone-200">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <a href="/" className="text-2xl font-bold text-stone-900 tracking-tight whitespace-nowrap">
            CSSBuy<span className="text-emerald-600">BR</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-emerald-700 rounded-lg hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              {t('nav_home')}
            </a>

            {/* Categories Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-emerald-700 rounded-lg hover:bg-stone-100 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl border-2 border-stone-200 py-2 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 hover:text-emerald-700 transition-colors"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <img
                        src={cat.image}
                        alt={t(cat.nameKey)}
                        className="w-8 h-8 rounded-md object-cover object-top shrink-0"
                      />
                      <span className="truncate">{t(cat.nameKey)}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/cssbuy-beginner-guide/"
              className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-emerald-700 rounded-lg hover:bg-stone-100 transition-colors whitespace-nowrap"
            >
              {t('nav_buying_guide')}
            </a>

            <button onClick={() => scrollToSection('guides')} className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-emerald-700 rounded-lg hover:bg-stone-100 transition-colors whitespace-nowrap">
              {t('nav_guides')}
            </button>

            {/* Main Website CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-3 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap font-semibold"
            >
              CSSBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="ml-1 px-4 py-2 text-sm bg-stone-800 text-white rounded-lg hover:bg-stone-900 transition-colors whitespace-nowrap font-semibold"
            >
              {t('btn_buy_it_now')}
            </a>

            {/* Language Switcher */}
            <div className="relative ml-3">
              <button
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="px-3 py-2 text-sm font-semibold text-stone-800 hover:text-emerald-700 rounded-lg hover:bg-stone-100 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                <i className="ri-global-line" />
                <span className="uppercase">{i18n.language === 'pt' ? 'PT' : i18n.language === 'de' ? 'DE' : 'EN'}</span>
              </button>
              {isLangOpen && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-white rounded-xl border-2 border-stone-200 py-2">
                  <button onClick={() => changeLanguage('pt')} className="block w-full text-left px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 hover:text-emerald-700 transition-colors">
                    {t('lang_pt')}
                  </button>
                  <button onClick={() => changeLanguage('de')} className="block w-full text-left px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 hover:text-emerald-700 transition-colors">
                    {t('lang_de')}
                  </button>
                  <button onClick={() => changeLanguage('en')} className="block w-full text-left px-4 py-2 text-sm text-stone-800 hover:bg-stone-50 hover:text-emerald-700 transition-colors">
                    {t('lang_en')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-stone-800 hover:text-emerald-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t-2 border-stone-200 px-4 py-4 space-y-3 max-h-[80vh] overflow-y-auto">
          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-2">
            <a
              href="/"
              className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-stone-800 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-home-4-line" />
              {t('nav_home')}
            </a>
            <a
              href="/cssbuy-beginner-guide/"
              className="flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold text-stone-800 bg-stone-100 rounded-lg hover:bg-stone-200 transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-shopping-bag-3-line" />
              {t('nav_buying_guide')}
            </a>
          </div>

          {/* Categories accordion */}
          <div className="rounded-xl border-2 border-stone-200 overflow-hidden">
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-stone-800 bg-stone-50"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line" />
                {t('nav_categories')}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${isMobileCatOpen ? 'rotate-180' : ''}`} />
            </button>
            {isMobileCatOpen && (
              <div className="grid grid-cols-2 gap-1 p-2">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-stone-50 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <img
                      src={cat.image}
                      alt={t(cat.nameKey)}
                      className="w-9 h-9 rounded-md object-cover object-top shrink-0"
                    />
                    <span className="text-xs text-stone-800 leading-tight">{t(cat.nameKey)}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Guide buttons */}
          <button onClick={() => scrollToSection('guides')} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-stone-800 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors">
            {t('nav_guides')}
          </button>
          <button onClick={() => scrollToSection('faq')} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-stone-800 rounded-lg bg-stone-100 hover:bg-stone-200 transition-colors">
            {t('nav_review')}
          </button>

          {/* CTA */}
          <div className="flex gap-2 pt-1">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex-1 px-4 py-2.5 text-sm bg-emerald-600 text-white rounded-lg text-center font-semibold whitespace-nowrap">
              CSSBuy Spreadsheet
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex-1 px-4 py-2.5 text-sm bg-stone-800 text-white rounded-lg text-center font-semibold whitespace-nowrap">
              {t('btn_buy_it_now')}
            </a>
          </div>

          {/* Language */}
          <div className="flex gap-2 pt-1">
            <button onClick={() => changeLanguage('pt')} className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${i18n.language === 'pt' ? 'border-emerald-600 text-emerald-700 bg-emerald-50' : 'border-stone-200 text-stone-800'}`}>
              PT
            </button>
            <button onClick={() => changeLanguage('de')} className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${i18n.language === 'de' ? 'border-emerald-600 text-emerald-700 bg-emerald-50' : 'border-stone-200 text-stone-800'}`}>
              DE
            </button>
            <button onClick={() => changeLanguage('en')} className={`flex-1 px-3 py-2 text-sm font-semibold rounded-lg border-2 ${i18n.language === 'en' ? 'border-emerald-600 text-emerald-700 bg-emerald-50' : 'border-stone-200 text-stone-800'}`}>
              EN
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}